import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { AlertCircle, Loader2, Shield, LogIn } from "lucide-react";

// Official Microsoft logo (4 colored squares) used per Microsoft brand guidelines.
const MicrosoftMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#f3f3f3" d="M0 0h23v23H0z" />
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

export default function Login() {
  const { loginMicrosoft } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState("");

  const handleMicrosoftSignIn = async () => {
    setError("");
    setSigningIn(true);
    try {
      await loginMicrosoft();
      // Page will redirect to Microsoft; nothing else to do here.
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to start Microsoft sign-in. Please try again.");
      setSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="login-page">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/dashboard-preview.jpeg')",
          filter: "blur(20px) brightness(0.35) saturate(0.6)",
          transform: "scale(1.1)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-slate-900/40 to-[#B71C1C]/20" />

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-[400px]">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 mb-2">
              <Shield size={20} className="text-white/80" />
              <span className="text-[11px] font-medium text-white/60 uppercase tracking-widest">
                Secure Access
              </span>
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight">TATA TRUSTS</h1>
            <p className="text-sm text-white/50 mt-1">Grant Management Dashboard</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h2 className="text-base font-bold text-slate-900 mb-1">Sign in</h2>
            <p className="text-xs text-slate-500 mb-6">
              Use your Tata Trusts Microsoft account to access the dashboard.
            </p>

            <button
              type="button"
              onClick={handleMicrosoftSignIn}
              disabled={signingIn}
              data-testid="microsoft-signin-btn"
              className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-[#2F2F2F] text-white rounded-lg text-sm font-semibold hover:bg-[#1f1f1f] disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.99] shadow-sm"
            >
              {signingIn ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Redirecting to Microsoft…
                </>
              ) : (
                <>
                  <MicrosoftMark size={18} />
                  Sign in with Microsoft
                  <LogIn size={14} className="opacity-70" />
                </>
              )}
            </button>

            {error && (
              <div
                className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg"
                data-testid="login-error"
              >
                <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-600 leading-relaxed">{error}</p>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-slate-100 text-center space-y-1">
              <p className="text-[10px] text-slate-400">
                Restricted to Tata Trusts Microsoft tenant.
              </p>
              <p className="text-[10px] text-slate-400">
                Authorized personnel only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
